<script setup lang="ts">
import { onMounted, ref } from "vue";
import { api, type ApiToken } from "../api";

const emit = defineEmits<{ error: [message: string] }>();
const tokens = ref<ApiToken[]>([]);
const name = ref("Codex weekly planner");
const creating = ref(false);
const revealed = ref("");
const copied = ref(false);
const scopes = ["workouts:read", "plans:read", "plans:write", "reports:read", "profile:write"];

async function load() {
  try { tokens.value = (await api.tokens()).tokens; }
  catch (caught) { emit("error", caught instanceof Error ? caught.message : "Could not load tokens"); }
}

async function create() {
  creating.value = true;
  try {
    const result = await api.createToken(name.value, scopes);
    revealed.value = result.token;
    await load();
  } catch (caught) { emit("error", caught instanceof Error ? caught.message : "Could not create token"); }
  finally { creating.value = false; }
}

async function copy() {
  await navigator.clipboard.writeText(revealed.value);
  copied.value = true;
  window.setTimeout(() => { copied.value = false; }, 1800);
}

async function revoke(token: ApiToken) {
  try { await api.revokeToken(token.id); await load(); }
  catch (caught) { emit("error", caught instanceof Error ? caught.message : "Could not revoke token"); }
}

onMounted(() => { void load(); });
</script>

<template>
  <details class="token-manager">
    <summary>Manage agent access</summary>
    <div class="token-panel">
      <p>Create a revocable credential for Codex or another workout-planning agent. It can read your completed history and publish plans, but cannot log gym sets.</p>
      <div v-if="revealed" class="token-reveal">
        <p class="eyebrow">Copy now · shown once</p><code>{{ revealed }}</code><button class="button primary" @click="copy">{{ copied ? "Copied" : "Copy token" }}</button>
      </div>
      <form class="token-create" @submit.prevent="create"><label>Token name<input v-model="name" required maxlength="80"></label><button class="button secondary" :disabled="creating">Create token</button></form>
      <div class="token-list">
        <article v-for="token in tokens" :key="token.id"><div><b>{{ token.name }}</b><code>{{ token.token_prefix }}…</code><small>{{ token.last_used_at ? `Used ${new Date(token.last_used_at).toLocaleDateString()}` : "Never used" }}</small></div><button class="text-button danger" @click="revoke(token)">Revoke</button></article>
        <p v-if="!tokens.length" class="muted">No active agent tokens.</p>
      </div>
    </div>
  </details>
</template>
